// Import types from generated API
import type {
  ClaimListItem as GeneratedClaimListItem,
  ClaimQueueCounts as GeneratedClaimQueueCounts,
  OrgBillingEncounterListResponse,
  OrgBillingEncounterResponse,
  OrgClaimListResponse as GeneratedOrgClaimListResponse,
} from '@store/generated/billing';
import type {
  BillingAddress5,
  ClaimAnnotation2,
  ClaimLine5,
  ClaimListResponse,
  ClaimPdfGenerateResponse,
  ClaimResponse2,
  CreateClaimRequest,
  InsuranceType5,
  Sex5,
  SubmitClaimRequest as SubmitClaimRequestType,
  SubmitClaimResponse as SubmitClaimResponseType,
  TaxIdType5,
  UpdateClaimRequest,
} from '@store/generated/claims';

import type { ClaimScrubberUIState } from './store/slice';

// Extend the root state via declaration merging
declare module '@store/types' {
  interface DynamicFeatureState {
    claimScrubber?: ClaimScrubberUIState;
  }
}

// New enriched claim types (org-scoped endpoints)
export type ClaimListItem = GeneratedClaimListItem;
export type ClaimQueueCounts = GeneratedClaimQueueCounts;
export type OrgClaimListResponse = GeneratedOrgClaimListResponse;

// Re-export with feature-friendly names (hiding version numbers)
export type Claim = ClaimResponse2;
export type ClaimList = ClaimListResponse;
export type BillingEncounter = OrgBillingEncounterResponse;
export type BillingEncounterList = OrgBillingEncounterListResponse;
export type ClaimPdf = ClaimPdfGenerateResponse;
export type ClaimAnnotation = ClaimAnnotation2;

// Use the latest versions without exposing the numbers
export type BillingAddress = BillingAddress5;
export type ClaimLine = ClaimLine5;
export type InsuranceType = InsuranceType5;
export type Sex = Sex5;
export type TaxIdType = TaxIdType5;

// Legacy type aliases for backward compatibility
export type ClaimLineType = ClaimLine;
export type BillingAddressType = BillingAddress;

// API request types
export type CreateClaim = CreateClaimRequest;
export type ClaimUpdateRequest = UpdateClaimRequest; // Clean name for update requests
export type SubmitClaimRequest = SubmitClaimRequestType;
export type SubmitClaimResponse = SubmitClaimResponseType;

// CMS-1500 Health Insurance Claim Form Types (Simplified for POC)

export interface CMS1500FormData {
  // Box 1: Insurance Type
  insuranceType: string;

  // Box 1a: Insured's ID Number
  insuredIdNumber: string;

  // Other insurance details (for Box 1 when "OTHER" is selected)
  otherInsurance?: {
    carrierName?: string;
    planId?: string;
  };

  // Boxes 2-8: Patient and Insured Basic Info
  patient: {
    lastName: string;
    firstName: string;
    middleInitial?: string;
    dateOfBirth: string;
    sex: string;
  };

  insured: {
    lastName: string;
    firstName: string;
    middleInitial?: string;
  };

  patientAddress: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    zipCode: string;
    telephone: string;
  };

  patientRelationship: string;

  insuredAddress: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    zipCode: string;
    telephone: string;
  };

  // Box 8: Reserved for NUCC Use
  reservedForNUCC?: string;

  // Box 9: Other Insured
  otherInsured?: {
    lastName?: string;
    firstName?: string;
    middleInitial?: string;
    policyGroupNumber?: string;
    dateOfBirth?: string;
    sex?: string;
    employerName?: string;
    insurancePlanName?: string;
    reservedNUCC9b?: string;
    reservedNUCC9c?: string;
  };

  // Box 10: Patient Condition
  patientCondition: {
    employment: boolean;
    autoAccident: boolean;
    autoAccidentState?: string;
    otherAccident: boolean;
    claimCodes?: string;
  };

  // Box 11: Insured's Policy Group
  insuredPolicyGroupNumber?: string;
  insuredDateOfBirth?: string;
  insuredSex?: string;
  insuredEmployerName?: string;
  insurancePlanName?: string;
  isThereAnotherBenefitPlan?: boolean;
  otherClaimId?: string;

  // Boxes 12-13: Signatures
  patientSignature: {
    signed: boolean;
    date: string;
  };

  insuredSignature?: {
    signed: boolean;
    signatureOnFile?: boolean;
    date?: string;
  };

  // Boxes 14-20: Various Dates
  currentIllnessDate?: string;
  currentIllnessQualifier?: string;
  otherDate?: string;
  otherDateQualifier?: string;
  unableToWorkDates?: {
    from: string;
    to: string;
  };
  referringProvider?: {
    name: string;
    npi?: string;
    qualifier?: string;
    otherID?: string;
  };
  hospitalizationDates?: {
    from: string;
    to: string;
  };
  additionalClaimInfo?: string;
  outsideLab: {
    used: boolean;
    charges?: string;
  };

  // Box 21: Diagnosis
  diagnosisCodes: Record<string, string>;
  icdIndicator: string;

  // Boxes 22-23: Resubmission
  resubmissionCode?: string;
  originalRefNumber?: string;
  priorAuthNumber?: string;

  // Box 24: Service Lines
  serviceLines: ServiceLine[];

  // Boxes 25-30: Payment Info
  federalTaxIdNumber: string;
  taxIdType: string;
  patientAccountNumber?: string;
  acceptAssignment: boolean;
  totalCharge: string;
  amountPaid?: string;

  // Boxes 31-32: Provider Info
  physicianSignature: {
    signed: boolean;
    date: string;
  };
  providerName?: string;
  providerCredentials?: string;
  facilityName?: string;
  facilityAddress?: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    zipCode: string;
  };
  facilityNpi?: string;
  facilityOtherId?: string;

  // Box 33: Billing Provider
  billingProvider: {
    name: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    zipCode: string;
    phone: string;
    npi: string;
    otherId?: string;
    otherIdQualifier?: string;
    pin?: string;
    groupNumber?: string;
    taxonomyCode?: string;
  };

  // Payer info (for backwards compatibility)
  payer?: {
    id: string;
    name: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    zipCode: string;
  };
}

export interface ServiceLine {
  dateOfService: {
    from: string;
    to: string;
  };
  placeOfService: string;
  emergency?: boolean;
  procedureCode: string;
  modifiers?: Record<string, string>;
  diagnosisPointers: string[];
  charges: string;
  units: string;
  epsdt?: string;
  renderingProviderNpi?: string;
}

// Place of Service Codes (CMS Official List)
export const PLACE_OF_SERVICE_CODES = {
  '01': 'Pharmacy',
  '02': 'Telehealth Provided Other than in Patient\'s Home',
  '03': 'School',
  '04': 'Homeless Shelter',
  '05': 'Indian Health Service Free-standing Facility',
  '06': 'Indian Health Service Provider-based Facility',
  '07': 'Tribal 638 Free-standing Facility',
  '08': 'Tribal 638 Provider-based Facility',
  '09': 'Prison/Correctional Facility',
  '10': 'Telehealth Provided in Patient\'s Home',
  '11': 'Office',
  '12': 'Home',
  '13': 'Assisted Living Facility',
  '14': 'Group Home',
  '15': 'Mobile Unit',
  '16': 'Temporary Lodging',
  '17': 'Walk-in Retail Health Clinic',
  '18': 'Place of Employment-Worksite',
  '19': 'Off Campus-Outpatient Hospital',
  '20': 'Urgent Care Facility',
  '21': 'Inpatient Hospital',
  '22': 'On Campus-Outpatient Hospital',
  '23': 'Emergency Room - Hospital',
  '24': 'Ambulatory Surgical Center',
  '25': 'Birthing Center',
  '26': 'Military Treatment Facility',
  '31': 'Skilled Nursing Facility',
  '32': 'Nursing Facility',
  '33': 'Custodial Care Facility',
  '34': 'Hospice',
  '41': 'Ambulance - Land',
  '42': 'Ambulance - Air or Water',
  '49': 'Independent Clinic',
  '50': 'Federally Qualified Health Center',
  '51': 'Inpatient Psychiatric Facility',
  '52': 'Psychiatric Facility-Partial Hospitalization',
  '53': 'Community Mental Health Center',
  '54': 'Intermediate Care Facility/Individuals with Intellectual Disabilities',
  '55': 'Residential Substance Abuse Treatment Facility',
  '56': 'Psychiatric Residential Treatment Center',
  '57': 'Non-residential Substance Abuse Treatment Facility',
  '58': 'Non-residential Opioid Treatment Facility',
  '60': 'Mass Immunization Center',
  '61': 'Comprehensive Inpatient Rehabilitation Facility',
  '62': 'Comprehensive Outpatient Rehabilitation Facility',
  '65': 'End-Stage Renal Disease Treatment Facility',
  '71': 'State or Local Public Health Clinic',
  '72': 'Rural Health Clinic',
  '81': 'Independent Laboratory',
  '99': 'Other Place of Service',
} as const;

// US States for dropdown
export const US_STATES = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  DC: 'District of Columbia',
} as const;
